import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase/config.js';
import { doc, getDoc, updateDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth } from '../firebase/config.js';
import { onAuthStateChanged } from 'firebase/auth';

const SubstituteRegistrationSetup = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sections');
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Default configuration structure
  const defaultConfig = {
    sections: {
      personalInfo: {
        enabled: true,
        title: "Personal Information",
        description: "Basic contact and personal details",
        fields: {
          firstName: { enabled: true, required: true, label: "First Name", type: "text" },
          lastName: { enabled: true, required: true, label: "Last Name", type: "text" },
          email: { enabled: true, required: true, label: "Email Address", type: "email" },
          phone: { enabled: true, required: true, label: "Phone Number", type: "tel" },
          address: { enabled: false, required: false, label: "Street Address", type: "text" },
          city: { enabled: false, required: false, label: "City", type: "text" },
          state: { enabled: false, required: false, label: "State", type: "text" },
          zip: { enabled: false, required: false, label: "ZIP Code", type: "text" }
        }
      },
      experience: {
        enabled: true,
        title: "Experience & Qualifications",
        description: "Your background and qualifications",
        fields: {
          education: { enabled: true, required: false, label: "Education Level", type: "select", options: ["High School", "Associate Degree", "Bachelor's Degree", "Master's Degree", "Doctorate"] },
          teachingLicense: { enabled: true, required: false, label: "Teaching License", type: "select", options: ["Yes", "No", "Expired"] },
          experience: { enabled: true, required: false, label: "Years of Experience", type: "number" },
          subjectAreas: { enabled: true, required: false, label: "Subject Areas", type: "multiselect", options: ["Math", "Science", "English", "History", "Art", "Music", "PE", "Special Education"] }
        }
      },
      availability: {
        enabled: true,
        title: "Availability",
        description: "When are you available to substitute?",
        fields: {
          daysAvailable: { enabled: true, required: true, label: "Days Available", type: "multiselect", options: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"] },
          gradePreferences: { enabled: true, required: false, label: "Grade Preferences", type: "multiselect", options: ["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"] },
          startDate: { enabled: true, required: false, label: "Available Start Date", type: "date" }
        }
      },
      emergency: {
        enabled: true,
        title: "Emergency Contact",
        description: "Emergency contact information",
        fields: {
          emergencyName: { enabled: true, required: true, label: "Emergency Contact Name", type: "text" },
          emergencyPhone: { enabled: true, required: true, label: "Emergency Contact Phone", type: "tel" },
          emergencyRelation: { enabled: true, required: false, label: "Relationship", type: "text" }
        }
      }
    },
    customFields: [],
    settings: {
      requireAccessCode: true,
      accessCode: "substitute2024",
      requireApproval: true,
      emailNotifications: true,
      welcomeMessage: "Thank you for your interest in substitute teaching!",
      termsAndConditions: "By registering, you agree to our substitute policies."
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      
      try {
        // Get user data from faculty_staff collection
        const userQuery = await getDocs(
          query(collection(db, 'faculty_staff'), where('email', '==', user.email))
        );
        
        if (!userQuery.empty) {
          const userData = userQuery.docs[0].data();
          setCurrentUserData({
            ...userData,
            uid: user.uid,
            email: user.email
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    if (user && !authLoading) {
      fetchUserData();
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (currentUserData) {
      loadConfiguration();
    }
  }, [currentUserData]);

  const loadConfiguration = async () => {
  if (!currentUserData?.tenantId) {
    setConfig(defaultConfig);
    setConfigLoading(false);
    return;
  }
  
  try {
    // Read from tenantConfigurations instead of tenant document
    const configDoc = await getDoc(
      doc(db, 'tenantConfigurations', `${currentUserData.tenantId}_substitute_registration`)
    );
    
    if (configDoc.exists()) {
      setConfig(configDoc.data().configValue || defaultConfig);
    } else {
      setConfig(defaultConfig);
    }
  } catch (error) {
    console.error('Error loading configuration:', error);
    setConfig(defaultConfig);
  }
  setConfigLoading(false);
};

  const saveConfiguration = async () => {
  setSaving(true);
  setMessage('');
  
  if (!user || !currentUserData?.tenantId) {
    setMessage('Error: User not authenticated or no tenant ID found');
    setSaving(false);
    return;
  }
  
  try {
    // Save to tenantConfigurations collection instead of tenant document
    await setDoc(doc(db, 'tenantConfigurations', `${currentUserData.tenantId}_substitute_registration`), {
      tenantId: currentUserData.tenantId,
      configKey: 'substitute_registration',
      configValue: config,
      updatedAt: new Date(),
      updatedBy: user.email
    });
    setMessage('Configuration saved successfully!');
  } catch (error) {
    console.error('Error saving configuration:', error);
    setMessage(`Error saving configuration: ${error.message}`);
  }
  
  setSaving(false);
  setTimeout(() => setMessage(''), 3000);
};

  const toggleSection = (sectionKey) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          enabled: !prev.sections[sectionKey].enabled
        }
      }
    }));
  };

  const toggleField = (sectionKey, fieldKey) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          fields: {
            ...prev.sections[sectionKey].fields,
            [fieldKey]: {
              ...prev.sections[sectionKey].fields[fieldKey],
              enabled: !prev.sections[sectionKey].fields[fieldKey].enabled
            }
          }
        }
      }
    }));
  };

  const toggleFieldRequired = (sectionKey, fieldKey) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          fields: {
            ...prev.sections[sectionKey].fields,
            [fieldKey]: {
              ...prev.sections[sectionKey].fields[fieldKey],
              required: !prev.sections[sectionKey].fields[fieldKey].required
            }
          }
        }
      }
    }));
  };

  const updateFieldLabel = (sectionKey, fieldKey, newLabel) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          fields: {
            ...prev.sections[sectionKey].fields,
            [fieldKey]: {
              ...prev.sections[sectionKey].fields[fieldKey],
              label: newLabel
            }
          }
        }
      }
    }));
  };

  const updateSetting = (key, value) => {
    setConfig(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [key]: value
      }
    }));
  };

  const addCustomField = () => {
    const newField = {
      id: `custom_${Date.now()}`,
      label: "New Custom Field",
      type: "text",
      required: false,
      enabled: true
    };
    
    setConfig(prev => ({
      ...prev,
      customFields: [...prev.customFields, newField]
    }));
  };

  const updateCustomField = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      customFields: prev.customFields.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  const removeCustomField = (index) => {
    setConfig(prev => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index)
    }));
  };

  if (authLoading || configLoading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading configuration...</div>;
  }

  return (
    <div style={{ padding: '40px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
  {/* Back Button */}
  <div style={{ marginBottom: '20px' }}>
    <Link 
      to="/admin"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        backgroundColor: '#6c757d',
        color: 'white',
        textDecoration: 'none',
        borderRadius: '4px',
        fontSize: '14px',
        fontWeight: '500'
      }}
    >
      ← Back to Admin
    </Link>
  </div>

  <h1 style={{ color: '#1976d2', marginBottom: '10px', fontSize: '32px' }}>Substitute Registration Setup</h1>
  <p style={{ color: '#666', marginBottom: '30px', fontSize: '16px' }}>
    Configure what information you collect from substitute applicants
  </p>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '30px', borderBottom: '2px solid #e0e0e0' }}>
          <div style={{ display: 'flex', gap: '0' }}>
            {[
              { key: 'sections', label: 'Form Sections' },
              { key: 'custom', label: 'Custom Fields' },
              { key: 'settings', label: 'Settings' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: activeTab === tab.key ? '#1976d2' : 'transparent',
                  color: activeTab === tab.key ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: activeTab === tab.key ? '600' : '400'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '30px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          
          {/* Form Sections Tab */}
          {activeTab === 'sections' && (
            <div>
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#1976d2' }}>Form Sections & Fields</h2>
              
              {Object.entries(config.sections).map(([sectionKey, section]) => (
                <div key={sectionKey} style={{ marginBottom: '30px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                  {/* Section Header */}
                  <div style={{ 
                    backgroundColor: section.enabled ? '#e3f2fd' : '#f5f5f5',
                    padding: '15px 20px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={section.enabled}
                          onChange={() => toggleSection(sectionKey)}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '18px', fontWeight: '600', color: section.enabled ? '#1976d2' : '#666' }}>
                          {section.title}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Fields */}
                  {section.enabled && (
                    <div style={{ padding: '20px' }}>
                      <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px' }}>{section.description}</p>
                      
                      <div style={{ display: 'grid', gap: '15px' }}>
                        {Object.entries(section.fields).map(([fieldKey, field]) => (
                          <div key={fieldKey} style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '30px 1fr 80px 120px', 
                            gap: '15px', 
                            alignItems: 'center',
                            padding: '10px',
                            backgroundColor: field.enabled ? '#fafafa' : '#f0f0f0',
                            borderRadius: '4px'
                          }}>
                            {/* Enable checkbox */}
                            <input
                              type="checkbox"
                              checked={field.enabled}
                              onChange={() => toggleField(sectionKey, fieldKey)}
                              style={{ width: '16px', height: '16px' }}
                            />
                            
                            {/* Field label (editable) */}
                            <input
                              type="text"
                              value={field.label}
                              onChange={(e) => updateFieldLabel(sectionKey, fieldKey, e.target.value)}
                              disabled={!field.enabled}
                              style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: field.enabled ? 'white' : '#f5f5f5'
                              }}
                            />
                            
                            {/* Field type */}
                            <span style={{ 
                              fontSize: '12px', 
                              color: '#666',
                              textAlign: 'center',
                              padding: '4px 8px',
                              backgroundColor: '#e3f2fd',
                              borderRadius: '12px'
                            }}>
                              {field.type}
                            </span>
                            
                            {/* Required toggle */}
                            <label style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              cursor: field.enabled ? 'pointer' : 'not-allowed',
                              fontSize: '14px'
                            }}>
                              <input
                                type="checkbox"
                                checked={field.required}
                                onChange={() => toggleFieldRequired(sectionKey, fieldKey)}
                                disabled={!field.enabled}
                                style={{ width: '14px', height: '14px' }}
                              />
                              Required
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Custom Fields Tab */}
          {activeTab === 'custom' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#1976d2' }}>Custom Fields</h2>
                <button
                  onClick={addCustomField}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  + Add Custom Field
                </button>
              </div>

              {config.customFields.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No custom fields added yet. Click "Add Custom Field" to create one.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '15px' }}>
                  {config.customFields.map((field, index) => (
                    <div key={field.id} style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '30px 1fr 120px 80px 40px', 
                      gap: '15px', 
                      alignItems: 'center',
                      padding: '15px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px'
                    }}>
                      {/* Enable checkbox */}
                      <input
                        type="checkbox"
                        checked={field.enabled}
                        onChange={(e) => updateCustomField(index, 'enabled', e.target.checked)}
                        style={{ width: '16px', height: '16px' }}
                      />
                      
                      {/* Field label */}
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateCustomField(index, 'label', e.target.value)}
                        placeholder="Field Label"
                        style={{
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                      
                      {/* Field type */}
                      <select
                        value={field.type}
                        onChange={(e) => updateCustomField(index, 'type', e.target.value)}
                        style={{
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      >
                        <option value="text">Text</option>
                        <option value="email">Email</option>
                        <option value="tel">Phone</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                        <option value="select">Dropdown</option>
                        <option value="multiselect">Multi-select</option>
                        <option value="textarea">Text Area</option>
                      </select>
                      
                      {/* Required toggle */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateCustomField(index, 'required', e.target.checked)}
                          style={{ width: '14px', height: '14px' }}
                        />
                        Required
                      </label>
                      
                      {/* Remove button */}
                      <button
                        onClick={() => removeCustomField(index)}
                        style={{
                          padding: '8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div>
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#1976d2' }}>Registration Settings</h2>
              
              <div style={{ display: 'grid', gap: '25px' }}>
                {/* Access Code */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.settings.requireAccessCode}
                      onChange={(e) => updateSetting('requireAccessCode', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>Require Access Code</span>
                  </label>
                  {config.settings.requireAccessCode && (
                    <input
                      type="text"
                      value={config.settings.accessCode}
                      onChange={(e) => updateSetting('accessCode', e.target.value)}
                      placeholder="Enter access code"
                      style={{
                        width: '300px',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginLeft: '26px'
                      }}
                    />
                  )}
                  <p style={{ margin: '5px 0 0 26px', fontSize: '14px', color: '#666' }}>
                    Prevents spam registrations by requiring a code
                  </p>
                </div>

                {/* Approval Required */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.settings.requireApproval}
                      onChange={(e) => updateSetting('requireApproval', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>Require Manual Approval</span>
                  </label>
                  <p style={{ margin: '5px 0 0 26px', fontSize: '14px', color: '#666' }}>
                    All registrations must be approved by an administrator
                  </p>
                </div>

                {/* Email Notifications */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.settings.emailNotifications}
                      onChange={(e) => updateSetting('emailNotifications', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>Send Email Notifications</span>
                  </label>
                  <p style={{ margin: '5px 0 0 26px', fontSize: '14px', color: '#666' }}>
                    Send emails when applications are submitted and approved
                  </p>
                </div>

                {/* Welcome Message */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', fontWeight: '500' }}>
                    Welcome Message
                  </label>
                  <textarea
                    value={config.settings.welcomeMessage}
                    onChange={(e) => updateSetting('welcomeMessage', e.target.value)}
                    placeholder="Enter welcome message for applicants"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Terms and Conditions */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', fontWeight: '500' }}>
                    Terms and Conditions
                  </label>
                  <textarea
                    value={config.settings.termsAndConditions}
                    onChange={(e) => updateSetting('termsAndConditions', e.target.value)}
                    placeholder="Enter terms and conditions text"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
            <button
              onClick={saveConfiguration}
              disabled={saving}
              style={{
                padding: '12px 30px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            
            {message && (
              <div style={{
                marginTop: '15px',
                padding: '10px',
                backgroundColor: message.includes('Error') ? '#f8d7da' : '#d4edda',
                color: message.includes('Error') ? '#721c24' : '#155724',
                borderRadius: '4px',
                display: 'inline-block'
              }}>
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubstituteRegistrationSetup;